import { useMutation } from '@tanstack/react-query';
import { useRouter } from '@tanstack/react-router';
import { useState } from 'react';
import { Button, Modal } from '@bb/ui';
import { ApiError, type AdminApi, type TranslationsImport } from '../../../api/client.ts';

export interface TranslationsImportDialogProps {
  environmentId: string;
  campaignId: string;
  /** `draft.version` of the campaign: the import writes into the draft and is refused if it changed meanwhile. No draft: export only. */
  draftVersion: number | undefined;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The employee holds `campaigns.edit`; without it the file can only be exported. */
  canImport?: boolean;
  /** After an import: how many cells were applied and the campaign with its updated draft. */
  onImported?: (result: TranslationsImport) => void;
}

/** Saves text as a file through a temporary link. */
export function saveTextFile(fileName: string, text: string, type = 'text/csv;charset=utf-8'): void {
  const url = URL.createObjectURL(new Blob([text], { type }));
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.append(link);
  link.click();
  link.remove();
  // Revoked after the click has started the download (some browsers cancel it when revoked at once).
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

const readText = (file: Blob): Promise<string> =>
  typeof file.text === 'function'
    ? file.text()
    : new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result));
        reader.onerror = () => reject(reader.error);
        reader.readAsText(file);
      });

const errorText = (error: unknown) => (error instanceof ApiError ? error.message : 'Something went wrong.');

const importErrorText = (error: unknown) => {
  if (!(error instanceof ApiError)) return 'Something went wrong.';
  if (error.code === 'translations_invalid') return 'Nothing was imported: fix these rows and upload the file again.';
  if (error.code === 'revision_conflict') return 'The campaign was changed since it was opened. Reload it and upload the file again.';
  return error.message;
};

const plural = (n: number, one: string, many: string) => `${n.toLocaleString('en-US')} ${n === 1 ? one : many}`;

/**
 * Translators' round trip of a campaign's texts (BB-55): download a CSV with one row per text field (key, default
 * text, a column per locale), and upload it back filled in. Filled cells become `draft` translations in the draft;
 * a file with any bad row imports nothing and the rows are listed.
 */
export function TranslationsImportDialog({ environmentId, campaignId, draftVersion, open, onOpenChange, canImport = true, onImported }: TranslationsImportDialogProps) {
  const { api } = useRouter().options.context as { api: AdminApi };
  const [fileName, setFileName] = useState<string | null>(null);
  const download = useMutation({
    mutationFn: () => api.translationsCsv(environmentId, campaignId),
    onSuccess: (csv) => saveTextFile(`translations-${campaignId}.csv`, csv),
  });
  const upload = useMutation({
    mutationFn: async (file: Blob) => api.importTranslations(environmentId, campaignId, draftVersion!, await readText(file)),
    onSuccess: (result) => onImported?.(result),
  });
  const rowErrors = upload.error instanceof ApiError && upload.error.code === 'translations_invalid' ? ((upload.error.problem.errors as Array<{ row: number; message: string }> | undefined) ?? []) : [];
  const blocked = !canImport ? 'Importing needs the campaigns.edit permission.' : draftVersion === undefined ? 'Import writes into the campaign’s draft, and it has none: start a draft of the campaign, then import.' : null;
  const close = () => {
    download.reset();
    upload.reset();
    setFileName(null);
    onOpenChange(false);
  };

  return (
    <Modal
      open={open}
      onOpenChange={(next) => (next ? onOpenChange(true) : close())}
      title="Translations"
      description="Export the texts for translators and import their file back."
      footer={
        <Button variant="ghost" onClick={close}>
          Close
        </Button>
      }
    >
      <section aria-label="Export translations">
        <h3>Export</h3>
        <p className="hint">A CSV with one row per text field of every variant and channel: its key, the default text and a column per locale.</p>
        <Button onClick={() => download.mutate()} loading={download.isPending}>
          Download CSV
        </Button>
        {download.isError && <p role="alert">The file could not be downloaded: {errorText(download.error)}</p>}
      </section>
      <section aria-label="Import translations">
        <h3>Import</h3>
        <p className="hint">Upload the file with the locale columns filled in. Filled cells become draft translations; the default column and empty cells change nothing. A file with errors imports nothing.</p>
        {blocked ? (
          <p className="hint">{blocked}</p>
        ) : (
          <label className="button">
            Upload CSV
            <input
              type="file"
              accept=".csv,text/csv"
              className="visually-hidden"
              aria-label="CSV file"
              disabled={upload.isPending}
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (!file) return;
                setFileName(file.name);
                upload.mutate(file);
                // The same file can be uploaded again after fixing it.
                e.target.value = '';
              }}
            />
          </label>
        )}
        {upload.isPending && <p aria-busy="true">Importing {fileName}…</p>}
        {upload.isSuccess && (
          <p role="status">
            {upload.data.updated === 0 ? 'No translations changed: the file has no filled locale cells.' : `${plural(upload.data.updated, 'translation', 'translations')} updated from ${fileName}. They are drafts until reviewed.`}
          </p>
        )}
        {upload.isError && (
          <div role="alert">
            <p>{importErrorText(upload.error)}</p>
            {rowErrors.length > 0 && (
              <ul aria-label="Import errors">
                {rowErrors.map((e) => (
                  <li key={`${e.row}/${e.message}`}>
                    Row {e.row}: {e.message}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </section>
    </Modal>
  );
}
